import { captureException } from '@sentry/node';

import { IErrorResponse } from '@r1-frontend/do-request/core/types';
import { BaseErrorBody, getErrorMessage } from '@r1-frontend/do-request/error/baseErrorBody';
import { isAxiosError } from '@r1-frontend/do-request/guards/isAxiosError';
import { isAxiosTimeoutError } from '@r1-frontend/do-request/guards/isAxiosTimeoutError';

import { isResponseCanceled } from '@r1-frontend/shared/helpers/isResponseCanceled';

const UNKNOWN_ERROR_ERROR_CODE = 'UNKNOWN_ERROR' as const;
const UNKNOWN_ERROR = {
    code: UNKNOWN_ERROR_ERROR_CODE,
    message: 'Неизвестная ошибка',
} as const;
/**
 * Отправляет информацию об ошибке в сентри
 * @param e
 */
const sendToSentry = (e: unknown) => {
    const isSsr = typeof window === 'undefined';

    if (isAxiosError(e)) {
        // Игнорируем 4хх ошибки при отправке в сентри
        if (e.response && (e.response?.status >= 400 || e.response?.status <= 499)) {
            return;
        }

        // Если запрос отменен не отправляем в Сентри
        if (isResponseCanceled(e.message)) {
            return;
        }

        const { baseURL, url, timeout } = e.config;

        const message = e.response?.data?.message;

        captureException(e, {
            extra: {
                message,
                baseURL,
                url,
                timeout,
            },
            tags: {
                place: 'api-query',
                'api-url': baseURL,
                isSsr,
            },
        });
    } else {
        captureException(e, {
            tags: {
                place: 'api-query (not axios error)',
                isSsr,
            },
        });
    }
};

/**
 * Парсит сообщение об ошибке
 * @param e - error object
 */
export const captureQueryError = (e: unknown): IErrorResponse<BaseErrorBody> => {
    sendToSentry(e);

    if (isAxiosTimeoutError(e)) {
        return {
            isSuccess: false,
            error: {
                code: e.name,
                message: e.message,
                unparsed: e,
            },
        };
    }

    if (isAxiosError(e) && e.response?.data) {
        const { data, status } = e.response;

        return {
            isSuccess: false,
            error: {
                code: data.code || data.status || UNKNOWN_ERROR.code,
                message: data.message || 'Не содержит message',
                status: Number(status),
                unparsed: e,
            },
        };
    }

    return {
        isSuccess: false,
        error: {
            code: UNKNOWN_ERROR.code,
            message: getErrorMessage(e) || UNKNOWN_ERROR.message,
            unparsed: e,
        },
    };
};
